import { 
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  doc,
  collection,
  Firestore,
  getDoc,
  Timestamp
} from "firebase/firestore";
import { processVersionData } from "./versionDataProcessor";
import { LocationVersion } from "@/types/campLocation";
import { getLatestLocationVersion as getLatestVersionFromStatus } from "./versionStatusService";

export const getLocationVersionHistory = async (db: Firestore, locationId: string) => {
  try {
    console.log("[versionHistoryService] Getting version history for location:", locationId);
    const locationRef = doc(db, "campLocations", locationId);
    const versionsRef = collection(db, "campLocationVersions");
    
    const q = query(
      versionsRef,
      where("locationRef", "==", locationRef),
      orderBy("timestamp", "desc")
    );
    
    const snapshot = await getDocs(q);
    
    if (snapshot.empty) {
      console.log("[versionHistoryService] No versions found for location:", locationId);
      return [];
    }
    
    const versions = snapshot.docs.map((doc) => processVersionData(doc));
    console.log(`[versionHistoryService] Found ${versions.length} versions for location ${locationId}`);
    
    // Double-check sort by timestamp to ensure correct ordering (newest first)
    const sortedVersions = [...versions].sort((a, b) => {
      // Parse timestamps to ensure proper comparison
      const dateA = typeof a.timestamp === 'string' ? new Date(a.timestamp).getTime() : a.timestamp;
      const dateB = typeof b.timestamp === 'string' ? new Date(b.timestamp).getTime() : b.timestamp;
      return dateB - dateA; // Newest first
    });
    
    console.log("[versionHistoryService] Sorted versions by timestamp, newest first");
    
    // Check if there are any versions without version numbers
    const hasVersionsWithoutNumbers = sortedVersions.some(v => 
      v.versionNumber === undefined || v.versionNumber === null
    );
    
    // Only recalculate version numbers for versions that don't have them
    // This preserves existing numbers during version deletion
    if (hasVersionsWithoutNumbers) {
      console.log("[versionHistoryService] Found versions without numbers, assigning only to those");
      
      // First, determine the highest version number currently in use
      let highestVersionNumber = 0;
      sortedVersions.forEach(v => {
        if (v.versionNumber && v.versionNumber > highestVersionNumber) {
          highestVersionNumber = v.versionNumber;
        }
      });
      
      // If no versions have numbers, use the count as the highest
      if (highestVersionNumber === 0) {
        highestVersionNumber = sortedVersions.length;
      }
      
      // Assign version numbers only to versions without them
      sortedVersions.forEach((version) => {
        if (version.versionNumber === undefined || version.versionNumber === null) {
          version.versionNumber = highestVersionNumber--;
          console.log(`[versionHistoryService] Assigned version number ${version.versionNumber} to version ${version.versionId}`);
        } else {
          console.log(`[versionHistoryService] Preserved existing version number ${version.versionNumber} for version ${version.versionId}`);
        }
      });
      
      console.log("[versionHistoryService] Preserved existing version numbers while assigning new ones to versions without numbers");
    } else {
      console.log("[versionHistoryService] All versions have numbers, preserving them");
    }
    
    return sortedVersions;
  } catch (error) {
    console.error("[versionHistoryService] Error getting location version history:", error);
    throw error;
  }
};

// Use the function from versionStatusService instead of duplicating
export const getLatestLocationVersion = getLatestVersionFromStatus;

// Get a specific version by ID
export const getVersionById = async (db: Firestore, versionId: string): Promise<LocationVersion | null> => {
  try {
    console.log("[versionHistoryService] Getting version by ID:", versionId);
    const versionDoc = await getDoc(doc(db, "campLocationVersions", versionId));
    
    if (versionDoc.exists()) {
      const version = processVersionData(versionDoc);
      
      // If version doesn't have a version number, try to get it from history
      if (version.versionNumber === undefined || version.versionNumber === null) {
        // We need the location ID to get the history
        const locationRef = version.locationRef;
        if (locationRef) {
          let locationId: string | null = null;
          
          if (typeof locationRef === 'string') {
            locationId = locationRef;
          } else if (locationRef.id) {
            locationId = locationRef.id;
          } else if (locationRef.path) {
            const pathParts = locationRef.path.split('/');
            locationId = pathParts[pathParts.length - 1];
          }
          
          if (locationId) {
            const history = await getLocationVersionHistory(db, locationId);
            const versionInHistory = history.find(v => v.versionId === version.versionId);
            if (versionInHistory && versionInHistory.versionNumber !== undefined) {
              return versionInHistory;
            }
          }
        }
      }
      
      return version;
    } else {
      console.log("[versionHistoryService] Version not found:", versionId);
      return null;
    }
  } catch (error) {
    console.error("[versionHistoryService] Error getting version by ID:", error);
    throw error;
  }
};
